import React, { useMemo } from "react";
import { Stack, Switch } from "@mui/material";
import ConfigHeader from "../ConfigHeader/config-header";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { ConfigBaseProps } from "../../../Pages/Basket/basket";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import { FormExists, GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import {
    ProductField,
    SettingRootField,
    UpdateItemProductRootOnBasket,
    UpdateItemSettingsRootOnBasket,
    UpdateItemStatusOnBasket,
} from "../../../Redux/Reducers/redux-slice-basket";
import { BasketItemStatus } from "../../../Core/Api/Types/basket-item-status";
import ConfigSaveButton from "../ConfigSaveButton/config-save-button";
import FormSection from "../../Forms/FormSection/form-section";
import ConfigFormIcon from "../ConfigFormIcon";
import { OrderProfileSettingsBase } from "../../../Core/Api/Types/order-profile-settings-base";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { GridColDef } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../Table/table";
import ConfigCommonSectionFormDelivery from "../ConfigCommon/config-common-section-form-delivery";
import { DbStorageInfos } from "../../../Core/Api/Types/db-storage-infos";
import { MailingListDto } from "../../../Core/Api/Types/get-mailing-list-response";
import { GetOrderProductReadableType } from "../../../Utils/helpers";
import { useTranslation } from "react-i18next";
interface ConfigArchiveHeritageTapeProps extends ConfigBaseProps {
    storages: DbStorageInfos[];
    maillists: MailingListDto[];
}

interface ArchiveHeritageTape extends OrderProfileSettingsBase {
    assets: AssetInfos[];
    ltoSizeInGigaByte: number;
}

interface AssetInfos {
    assetId: number;
    isSelected: boolean;
    sizeInGigaByte: number;
    asset: DbAssetObjectInfos;
}

const ConfigArchiveHeritageTape: React.FC<ConfigArchiveHeritageTapeProps> = (props) => {
    const { t } = useTranslation();
    const { handleSubmit, handleCancel, handleChangeProduct, item, storages, maillists, ...otherProps } = props;
    const formId = `form-antipiracy-yt-${item.id}`;
    const formValid = useAppSelector(GetFormIsValid(formId));
    const formExists = useAppSelector(FormExists(formId));
    const dispatch = useAppDispatch();
    //   const apiGrid = useGridApiRef();

    // Item is updated automatically as it's passed as prop from parent and from redux orignally
    const settings: ArchiveHeritageTape = item.selectedProduct?.settings;

    // Update product root base object into redux
    const handleUpdateProductRoot = (field: ProductField, value: any) => {
        dispatch(UpdateItemProductRootOnBasket({ itemId: item.id, field: field, value: value }));
    };

    const formValidTrigger = useMemo(() => {
        // In case of invalid, update item status
        if (!formValid && formExists) dispatch(UpdateItemStatusOnBasket({ itemId: item.id, status: BasketItemStatus.New }));

        return formValid;
    }, [formValid]);

    const isConfigureSectionValid =
        settings.assets
            .filter((x) => x.isSelected)
            .reduce((accumulator, currentValue) => accumulator + currentValue.sizeInGigaByte, 0) < settings.ltoSizeInGigaByte;

    const cols: GridColDef[] = React.useMemo(
        () => [
            {
                headerName: t("common:select"),
                field: "asset.isSelected",
                flex: CellSize.small,
                minWidth: CellSize.small,
                valueGetter: (data: any, row: any) => row?.isSelected,
                renderCell: (data: any) => (
                    <Switch
                        size="small"
                        checked={data.value}
                        onChange={() => {
                            dispatch(
                                UpdateItemSettingsRootOnBasket({
                                    itemId: item.id,
                                    field: SettingRootField.ArchiveHeritageTape,
                                    value: data?.row?.assetId,
                                })
                            );
                        }}
                    />
                ),
            },
            {
                headerName: t("common:name"),
                field: "asset.name",
                flex: CellSize.xlarge,
                minWidth: CellSize.xlarge,
                valueGetter: (data: any, row: any) => row?.asset.name,
            },
            {
                headerName: t("common:type"),
                field: "asset.type",
                flex: CellSize.small,
                minWidth: CellSize.small,
                valueGetter: (data: any, row: any) => row?.asset.type,
            },
            {
                headerName: t("common:size"),
                field: "sizeInGigaByte",
                flex: CellSize.medium,
                minWidth: CellSize.medium,
                // renderCell: (data: any) => data.value,
            },
        ],
        []
    );

    return (
        <Stack width={"100%"} {...otherProps}>
            <ConfigHeader
                configType={item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.ArchiveHeritageTape}
                title={GetOrderProductReadableType(
                    item.selectedProduct?.type ? item.selectedProduct?.type : OrderProductType.ArchiveHeritageTape
                )}
                handleCancel={handleCancel}
                handleChangeProduct={handleChangeProduct}
                itemId={item.id}
            />
            <Stack spacing={1.5} width={"100%"} {...otherProps}>
                <FormSection
                    icon={<ConfigFormIcon icon="meta" />}
                    title={t("form:section.title.configKdmGeneration")}
                    status={isConfigureSectionValid ? "complete" : "warning"}
                    statusText={
                        (isConfigureSectionValid ? t("common:configured") : t("form:statusText.selectedAssetsExceedsLtosize")) +
                        " " +
                        settings.assets
                            .filter((x) => x.isSelected)
                            .reduce((accumulator, currentValue) => accumulator + currentValue.sizeInGigaByte, 0) +
                        "GB/ " +
                        settings.ltoSizeInGigaByte +
                        "GB"
                    }
                    sx={{ width: "100%" }}
                >
                    <Stack spacing={1} width={"100%"} {...otherProps}>
                        <DataTable
                            columns={cols}
                            rows={settings.assets.map((x) => {
                                return { ...x, id: x.assetId };
                            })}
                            rowHeight={64}
                            getRowHeight={() => "auto"}
                            pagination={true}
                            initialState={{
                                sorting: {
                                    sortModel: [{ field: "name", sort: "asc" }],
                                },
                                pinnedColumns: { left: ["__check__"], right: ["actions"] },
                            }}
                        />
                    </Stack>
                </FormSection>
            </Stack>
            <ConfigCommonSectionFormDelivery item={item} formid={formId} storages={storages} maillists={maillists} />
            <ConfigSaveButton item={item} onSubmit={handleSubmit} disabled={!isConfigureSectionValid} />
        </Stack>
    );
};

export default ConfigArchiveHeritageTape;
